"use client";

import { useEffect, useRef } from "react";

/**
 * Lightweight session replay — captures the last 30 seconds of user
 * interactions (clicks, scrolls, navigations, console errors) in a
 * circular buffer. When the ErrorTracker fires a client-side error
 * report, it attaches the replay buffer as context so we can see
 * what the user did leading up to the crash.
 *
 * This is NOT a full session replay tool (no mouse movement recording,
 * no DOM snapshots). It's a minimal breadcrumb trail for debugging.
 *
 * Mount this once in the root layout alongside <ErrorTracker />.
 */

interface ReplayEvent {
  t: number; // timestamp (ms since page load)
  type: string; // click, scroll, navigate, console, resize, input
  data?: string; // truncated detail
}

const BUFFER_SIZE = 200; // max events to keep
const BUFFER_TTL_MS = 30_000; // discard events older than 30s

// Global buffer — accessible to ErrorTracker via window.__replayBuffer
const buffer: ReplayEvent[] = [];

function pushEvent(type: string, data?: string) {
  const t = Math.round(performance.now());
  buffer.push({ t, type, data: data?.slice(0, 200) });

  // Trim to size limit
  while (buffer.length > BUFFER_SIZE) buffer.shift();

  // Trim by TTL
  const cutoff = t - BUFFER_TTL_MS;
  while (buffer.length > 0 && buffer[0].t < cutoff) buffer.shift();
}

export function getReplayBuffer(): ReplayEvent[] {
  return [...buffer];
}

export default function SessionReplay() {
  const mounted = useRef(false);

  useEffect(() => {
    if (mounted.current) return;
    mounted.current = true;

    // Expose buffer globally so ErrorTracker can attach it
    (window as unknown as Record<string, unknown>).__getReplayBuffer = getReplayBuffer;

    // Click tracking
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const tag = target.tagName?.toLowerCase() || "unknown";
      const text = (target.textContent || "").trim().slice(0, 50);
      const className = (target.className || "").toString().slice(0, 50);
      pushEvent("click", `${tag}${text ? `: "${text}"` : ""}${className ? ` .${className.split(" ")[0]}` : ""}`);
    };

    // Scroll tracking (debounced)
    let scrollTimeout: ReturnType<typeof setTimeout>;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        pushEvent("scroll", `y=${Math.round(window.scrollY)}`);
      }, 300);
    };

    // Navigation tracking
    const handlePopState = () => {
      pushEvent("navigate", window.location.pathname);
    };

    // Console error tracking
    const origError = console.error;
    console.error = (...args: unknown[]) => {
      pushEvent("console.error", String(args[0]).slice(0, 200));
      origError.apply(console, args);
    };

    // Input tracking (debounced, no values — just that input happened)
    const handleInput = (e: Event) => {
      const target = e.target as HTMLElement;
      const tag = target.tagName?.toLowerCase();
      const name = (target as HTMLInputElement).name || (target as HTMLInputElement).id || "";
      pushEvent("input", `${tag}${name ? `[${name}]` : ""}`);
    };

    // Initial navigation event
    pushEvent("navigate", window.location.pathname);

    document.addEventListener("click", handleClick, { passive: true });
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("popstate", handlePopState);
    document.addEventListener("input", handleInput, { passive: true });

    return () => {
      document.removeEventListener("click", handleClick);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("popstate", handlePopState);
      document.removeEventListener("input", handleInput);
      console.error = origError;
    };
  }, []);

  return null; // No visible UI
}
